import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useLazyQuery, useQuery } from '@apollo/client';

import { getPublicUser } from 'lane-shared/graphql/query';
import { convertToUUID } from 'uuid-encoding';

import { Flex, TabStrip } from 'components';
import { TabItem } from 'components/general/TabStrip';
import { EntityNotesAndHours } from 'domains/workOrder/serviceRequest/components/EntityNotesAndHours';

import { getTaskAuditLogsQuery, getTaskByIdQuery } from 'graphql-queries';
import { ClientTask } from 'graphql-query-contracts';

import { StepExecutionForm } from 'domains/workOrder/preventiveMaintenance/schedule/components';
import { buildTaskActions } from '../../hooks/buildTaskActions';
import { buildTaskDetailsViewModel } from '../../hooks/buildTaskDetailsViewModel';
import { buildTaskTabs, TaskTabsEnum } from '../../hooks/buildTaskTabs';
import { useExportPmTaskPdf } from '../../hooks/useExportPmTaskPdf';
import { usePmTaskAssignment } from '../../hooks/usePmTaskAssignment';
import { usePmTaskPermissions } from '../../hooks/usePmTaskPermissions';
import { usePmTaskStepExecution } from '../../hooks/usePmTaskStepExecution';
import { usePmTaskUpdate } from '../../hooks/usePmTaskUpdate';

import { usePmTaskAttachmentAccess } from 'react-hooks';
import { ActivityTab } from '../../components/Activity/Activity';
import { ServiceRequestSidePanel } from './components/ServiceRequestSidePanel';
import { TaskAttachments } from './components/TaskAttachments';
import { TaskDetailsHeader } from './components/TaskDetailsHeader';
import { TaskDetailsSummary } from './components/TaskDetailsSummary';
import { TaskEquipmentList } from './components/TaskEquipmentList';
import styles from './index.scss';

export function TaskDetailsPage({ channel }: any) {
  const { t } = useTranslation();
  const { taskId } = useParams<{ taskId: string }>();
  const { currentSteps, handleStepResponseChange, syncStepsFromTask } =
    usePmTaskStepExecution(taskId);

  const { data, loading, refetch } = useQuery(getTaskByIdQuery, {
    variables: {
      id: convertToUUID(taskId),
    },
    onCompleted: () => {
      syncStepsFromTask(data?.task);
    },
  });

  const {
    data: activityLogData,
    loading: auditLogLoading,
    refetch: refetchAuditLogs,
  } = useQuery(getTaskAuditLogsQuery, {
    variables: {
      taskId: convertToUUID(taskId),
    },
    fetchPolicy: 'network-only',
  });

  const task: ClientTask = data?.task;
  const {
    canRead: canReadAttachments,
    canCreate: canCreateAttachments,
    canDelete: canDeleteAttachments,
  } = usePmTaskAttachmentAccess({ ...task, id: taskId });

  const {
    canViewAllTasks,
    canUploadAttachments,
    canEditStatus,
    canAssignIndividual,
    canAssignTeam,
    hasEquipmentAccess,
    hasScheduleAccess,
    canBeAssigned,
    canCreateServiceRequest,
  } = usePmTaskPermissions(channel?._id);

  const [publicUser, { data: userData }] = useLazyQuery(getPublicUser);

  const isEquipmentEnabled = channel?.settings?.hasWorkOrderEquipmentEnabled;

  const tabs = buildTaskTabs({ t });

  const [selectedTab, setSelectedTab] = useState<TabItem>(tabs[0]!);
  const [
    isServiceRequestFormSidePanelOpen,
    setIsServiceRequestFormSidePanelOpen,
  ] = useState(false);

  const handleTabChange = (tab: TabItem) => {
    setSelectedTab(tab);
  };

  useEffect(() => {
    if (task?.createdBy) {
      publicUser({
        variables: {
          id: convertToUUID(task?.createdBy),
        },
      });
    }
  }, [task?.createdBy]);

  const {
    updateTask,
    refetchData,
    onTaskUpdated,
    handleAttachmentCreated,
    handleAttachmentDeleted,
  } = usePmTaskUpdate({ task, refetch, refetchAuditLogs });

  const assignment = usePmTaskAssignment({
    channelId: channel?._id,
    task,
    canAssignIndividual,
    canBeAssigned,
    updateTask,
  });

  const { handleExport } = useExportPmTaskPdf({
    task,
    refetch,
    refetchAuditLogs,
    createdUser: userData?.user,
    auditLogs: activityLogData?.taskAuditLogs,
  });

  const actions = buildTaskActions({
    canCreateServiceRequest,
    onExport: handleExport,
    onGenerateServiceRequest: () => setIsServiceRequestFormSidePanelOpen(true),
    t,
  });

  const viewModel = buildTaskDetailsViewModel({
    task,
    createdUser: userData?.user,
    channelSlug: channel?.slug,
    canViewAllTasks,
    hasScheduleAccess,
  });

  return (
    <div className={styles.TaskDetailsPage}>
      <TaskDetailsHeader
        task={task}
        viewModel={viewModel}
        assignment={assignment}
        actions={actions}
        canEditStatus={canEditStatus}
        canAssignTeam={canAssignTeam}
        canAssignIndividual={canAssignIndividual}
        canBeAssigned={canBeAssigned}
        loading={loading}
        auditLogLoading={auditLogLoading}
        onStatusChanged={refetchData}
      />

      <ServiceRequestSidePanel
        isOpen={isServiceRequestFormSidePanelOpen}
        onClose={() => setIsServiceRequestFormSidePanelOpen(false)}
      />

      <Flex direction="column">
        <TabStrip
          tabs={tabs}
          selected={selectedTab}
          onSelectTab={handleTabChange}
          className={styles.taskTabStrip}
          fullWidth
          skipLabelTranslation
        />
      </Flex>

      {selectedTab.value === TaskTabsEnum.Details ? (
        <Flex direction="column">
          <TaskDetailsSummary viewModel={viewModel} />
          <TaskEquipmentList
            equipment={task?.equipment}
            channelSlug={channel?.slug}
            hasEquipmentAccess={hasEquipmentAccess}
            isEquipmentEnabled={isEquipmentEnabled}
          />
          {canReadAttachments && (
            <TaskAttachments
              taskId={taskId}
              canAddAttachments={canUploadAttachments && canCreateAttachments}
              canDeleteAttachments={canDeleteAttachments}
              onAttachmentCreated={handleAttachmentCreated}
              onAttachmentDeleted={handleAttachmentDeleted}
            />
          )}
        </Flex>
      ) : null}

      {selectedTab.value === TaskTabsEnum.Steps ? (
        <StepExecutionForm
          channel={channel}
          steps={currentSteps}
          onStepChange={handleStepResponseChange}
          task={task}
        />
      ) : null}

      {selectedTab.value === TaskTabsEnum.TimeAndNotes ? (
        <EntityNotesAndHours
          entityData={viewModel.timeAndNotes}
          onEntityUpdated={onTaskUpdated}
        />
      ) : null}

      {selectedTab.value === TaskTabsEnum.Activity && (
        <ActivityTab testId="tabActivity" taskId={taskId} />
      )}
    </div>
  );
}
